import { Grid, List } from '@material-ui/core';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CardMessage from '../../../../../components/CardMessage/CardMessage';
import SelectPeriod from '../../../../../components/SelectPeriod/index';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import { PeriodOccurrencesEnum } from '../../../../../models/enum/PeriodOccurrencesEnum';
import { AppState } from '../../../../../store';
import { getPeriodOccurrencesStatus } from '../../../../../store/actions/punchCardActions';
import { CondominiumReducerData } from '../../../../../store/reducers/condominiumReducer';
import { PunchCardReducerData } from '../../../../../store/reducers/punchCardReducer';
import { convertMinutesToHours } from '../../../../../utils/convertMinutesToHours';
import OccurrencesListItem from './OccurrencesListItem';
import OccurrencesPeriodDetailedStatusDialog from './OccurrencesPeriodDetailedStatusDialog';

const OccurrencesPeriodStatusList: React.FC = () => {
  const dispatch = useDispatch();
  const [
    openReferencePunchCardDialog,
    setOpenReferencePunchCardDialog,
  ] = useState(false);
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [occurrencesTypeSelected, setOccurrencesTypeSelected] = useState<
    string[]
  >([]);
  const [dialogTitle, setDialogTitle] = useState<string>('');

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const {
    loadingOccurrencesPeriodStatus,
    periodOccurrencesStatus,
  }: PunchCardReducerData = useSelector(
    (state: AppState) => state.punchCardReducer,
  );

  const handleReferencePunchCardDialogOpen = (
    occurrencesType: string[],
    title = '',
  ) => {
    setOccurrencesTypeSelected(occurrencesType);
    setDialogTitle(title);
    setOpenReferencePunchCardDialog(true);
  };

  const handleChangePeriodSelected = useCallback(
    (period: PeriodDTO) => {
      if (condominiumSelected && period) {
        setPeriodSelected(period);
        dispatch(
          getPeriodOccurrencesStatus(
            moment(period.inicioMesApuracao, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            moment(period.fimMesApuracao, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            condominiumSelected.referencia,
          ),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [condominiumSelected],
  );

  return (
    <>
      <OccurrencesPeriodDetailedStatusDialog
        open={openReferencePunchCardDialog}
        title={dialogTitle}
        period={periodSelected}
        occurrencesTypeSelected={occurrencesTypeSelected}
        showDialogDataByPeriod
        handleClose={() => setOpenReferencePunchCardDialog(false)}
      />

      <Grid container alignItems="center" justify="flex-end">
        <Grid item>
          <SelectPeriod
            fullWidth
            disableUnderline
            value={periodSelected}
            handlePeriodChange={(period: PeriodDTO) =>
              handleChangePeriodSelected(period)
            }
          />
        </Grid>
      </Grid>

      <List>
        {!periodOccurrencesStatus && !loadingOccurrencesPeriodStatus ? (
          <CardMessage
            icon={
              <SentimentDissatisfiedIcon
                style={{ fontSize: 100, opacity: 0.4 }}
              />
            }
            message="Nenhum resultado"
          />
        ) : (
          <>
            <OccurrencesListItem
              title="Ocorrências Pendentes"
              mainText={periodOccurrencesStatus?.quantidadeOcorrenciasPendentes}
              descriptionText={`De ${periodOccurrencesStatus?.quantidadeTotal} funcionários`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen([], 'Ocorrências Pendentes')
              }
            />

            <OccurrencesListItem
              title="Horas Extras 200%"
              show={
                periodOccurrencesStatus?.horasHorasExtras200 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras200 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras200,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [PeriodOccurrencesEnum.HORAS_EXTRAS_200.description],
                  'Horas Extras 200%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 140%"
              show={
                periodOccurrencesStatus?.horasHorasExtras140 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras140 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras140,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [PeriodOccurrencesEnum.HORAS_EXTRAS_140.description],
                  'Horas Extras 140%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 100%"
              show={
                periodOccurrencesStatus?.horasHorasExtras100 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras100 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras100,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.HORAS_EXTRAS_100.description,
                    PeriodOccurrencesEnum.HORAS_EXTRAS_100_NOTURNO.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_100.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_100_NOTURNO
                      .description,
                  ],
                  'Horas Extras 100%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 80%"
              show={
                periodOccurrencesStatus?.horasHorasExtras80 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras80 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras80,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.HORAS_EXTRAS_80.description,
                    PeriodOccurrencesEnum.HORAS_EXTRAS_80_NOTURNO.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_80.description,
                  ],
                  'Horas Extras 80%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 75%"
              show={
                periodOccurrencesStatus?.horasHorasExtras75 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras75 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras75,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.HORAS_EXTRAS_75.description,
                    PeriodOccurrencesEnum.HORAS_EXTRAS_75_NOTURNO.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_75.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_75_NOTURNO
                      .description,
                  ],
                  'Horas Extras 75%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 60%"
              show={
                periodOccurrencesStatus?.horasHorasExtras60 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras60 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras60,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.HORAS_EXTRAS_60.description,
                    PeriodOccurrencesEnum.HORAS_EXTRAS_60_NOTURNO.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_60.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_60_NOTURNO
                      .description,
                  ],
                  'Horas Extras 60%',
                )
              }
            />

            <OccurrencesListItem
              title="Horas Extras 50%"
              show={
                periodOccurrencesStatus?.horasHorasExtras50 !== undefined &&
                periodOccurrencesStatus.horasHorasExtras50 > 0
              }
              mainText={convertMinutesToHours(
                periodOccurrencesStatus?.horasHorasExtras50,
              )}
              descriptionText={`${periodOccurrencesStatus?.quantidadeHorasExtras} funcionários em horas extras`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.HORAS_EXTRAS_50.description,
                    PeriodOccurrencesEnum.HORAS_EXTRAS_50_NOTURNO.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_50.description,
                    PeriodOccurrencesEnum.HORAS_EXTRA_REFEICAO_50_NOTURNO
                      .description,
                  ],
                  'Horas Extras 50%',
                )
              }
            />

            <OccurrencesListItem
              title="Faltas"
              mainText={periodOccurrencesStatus?.quantidadeFaltasTotal}
              descriptionText={`De ${periodOccurrencesStatus?.quantidadeTotal} funcionários`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.FALTA_SEM_JUSTIFICATIVA.description,
                    PeriodOccurrencesEnum.FALTAS_JUSTIFICADAS.description,
                  ],
                  'Faltas',
                )
              }
            />

            <OccurrencesListItem
              title="Atrasos"
              mainText={periodOccurrencesStatus?.quantidadeAtrasosTotal}
              descriptionText={`De ${periodOccurrencesStatus?.quantidadeTotal} funcionários`}
              loading={loadingOccurrencesPeriodStatus}
              onClick={() =>
                handleReferencePunchCardDialogOpen(
                  [
                    PeriodOccurrencesEnum.ATRASOS_SEM_JUSTIFICATIVA.description,
                    PeriodOccurrencesEnum.ATRASOS_JUSTIFICADOS.description,
                  ],
                  'Atrasos',
                )
              }
            />

            <OccurrencesListItem
              title="Pendências de Assinatura"
              mainText={periodOccurrencesStatus?.quantidadePendenteAssinatura}
              descriptionText={`De ${periodOccurrencesStatus?.quantidadeTotal} funcionários`}
              loading={loadingOccurrencesPeriodStatus}
              // onClick={() =>
              //   handleReferencePunchCardDialogOpen(
              //     'Pendências de Assinatura',
              //     'Pendências de Assinatura',
              //     true,
              //   )
              // }
            />
          </>
        )}
      </List>
    </>
  );
};

export default OccurrencesPeriodStatusList;
